import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NextStepsInfoDeviceTemplate } from '../components/next-steps/next-steps.component';
import { Subject, Subscription, tap } from 'rxjs';
import { MatSidenav } from '@angular/material/sidenav';
import { ImeiInfoDeviceType } from '../components/imei-info/imei-info.component';
import { ImeiLookupDeviceTemplate, ImeiResult } from '../components/imei-lookup/imei-lookup.component';
import { DeviceInfoFormResult } from '../components/cellular-device-info-form/cellular-device-info-form.component';
import { UpdateDeviceBodyDto } from 'src/app/core/services/device-inventory/update-device-body.dto';
import { ADPResourceService, StorageService } from 'digital-core';
import { DeviceInventoryService } from 'src/app/core/services/device-inventory.service';
import { SelectedDeviceService } from 'src/app/shared/device/selected-device.service';
import { AccountService } from 'src/app/core/services/account.service';
import { LoggedInUserSettings } from 'src/app/core/services/settings/logged-in-user-settings';
import { DeviceDto } from 'src/app/core/services/device-inventory/device.dto';
import { UtilityService } from 'src/app/core/services/utility.service';
import { PghUserDeviceBase } from 'src/app/shared/device/pgh-user-device';
import { ContractDeviceInventories } from 'src/app/core/services/contract-device-inventories';
import { ConfirmationDialogData } from 'src/app/shared/components/confirmation-dialog/confirmation-dialog-data';
import { ConfirmationDialogComponent } from 'src/app/shared/components/confirmation-dialog/confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { dashboardRouteWithSlash } from 'src/app/route-constants';

@Component({
  selector: 'pgh-phone-onboarding',
  templateUrl: './phone-onboarding.component.html',
  styleUrls: ['./phone-onboarding.component.scss']
})
export class PhoneOnboardingComponent implements OnInit, OnDestroy {
  private onDestroy$ = new Subject<void>();

  states = {
    LandingPage: 1,
    NextSteps: 2,
    ConfirmNumber: 3,
    ImeiInfo: 4,
    ProvideAdditionalInfo: 5,
    TextMessage: 6,
    AdditionalDeviceQuestionnaire: 7,
    AdditionalDeviceSelection: 8
  }

  @ViewChild('imeisidenav') sidenav: MatSidenav;
  events: string[] = [];
  opened: boolean;

  code: string;
  state = this.states.LandingPage;
  phoneNumber: string;
  imeiStepResult: ImeiResult;
  isBackVisible: boolean = false;
  private device: PghUserDeviceBase;

  public devicesFromAllInventories?: DeviceDto[];
  private subscriptions: Subscription[] = [];

  private deviceInventories?: ContractDeviceInventories;

  private registerDeviceInProgress = false;

  public imeiInfoDeviceType:ImeiInfoDeviceType=ImeiInfoDeviceType.Smartphone;
  public nextStepsInfoDeviceTypeTemplate:NextStepsInfoDeviceTemplate=NextStepsInfoDeviceTemplate.Smartphone;
  public imeiLookupDeviceTypeTemplate:ImeiLookupDeviceTemplate=ImeiLookupDeviceTemplate.Smartphone;
  
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    public resourceService: ADPResourceService,
    private utilityService: UtilityService,
    private deviceInventoryService: DeviceInventoryService,
    private selectedDeviceService: SelectedDeviceService,
    private accountService: AccountService,
    private loggedInUserSettingsService: LoggedInUserSettings,
    private storageService: StorageService,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.accountService.getAllInventoriesWithDevicesForSelectedContract(this.onDestroy$).subscribe({
      next: (inventories) => {
        this.deviceInventories = inventories;

        this.devicesFromAllInventories = [
          ...(inventories?.cellularEnabledPolicyInventory?.devices ?? []),
          ...(inventories?.gadgetPolicyInventory?.devices ?? []),
          ...(this.deviceInventoryService?.getEprismDevices()),
        ];
      },
      error: (error) => {
        throw error;
      }
    });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  changeState(state: any): void {
    if(state != this.states.LandingPage && state != this.states.TextMessage && state != this.states.AdditionalDeviceQuestionnaire){
      this.isBackVisible = true;
    }
    else{
      this.isBackVisible = false;
    }

    this.state = state;
    this.gotoTop();
  }

  //Special case when navigating to the Text Message stage of the flow, need to also pass
  //the phone number the user input on the form that will be receiving the text message
  goToTextScreen(e: any): void {
    this.phoneNumber = e.phoneNumber;
    this.changeState(e.state);
  }

  goToImeiLookup(e: any): void {
    this.phoneNumber = e.data[0].phoneNumber;
    this.device = e.data[0].device;
    this.changeState(e.state);
  }

  goToAdditionalDeviceQuestionnaire(e: any): void {
    this.changeState(e.state);
  }

  goToAdditionalDeviceSelection(e: any): void {
    this.router.navigate(['additional-device-selection'],{ queryParams: { isOnboardingFlow: 'true', isAdditionalDeviceQuestionnaireFlow: 'true'}});
  }

  goToAdditionalDeviceRegistration(e: any): void {
    if(e.data === 'TABL'){
      this.router.navigate(['tablet-registration'],{ queryParams: { isOnboardingFlow: 'true'}});
    }
    else if(e.data === 'SMAR'){
      this.router.navigate(['smartwatch-registration'],{ queryParams: { isOnboardingFlow: 'true'}});
    }
    else if(e.data === 'SMPH'){
      this.router.navigate(['phone-registration'],{ queryParams: { isOnboardingFlow: 'true'}});
    }
  }

  gotoBack() {
    if (this.state == this.states.NextSteps) {
      this.state = this.states.LandingPage
      this.isBackVisible = false;
    }
    else if(this.state == this.states.ConfirmNumber) {
        this.state = this.states.NextSteps;
        this.isBackVisible = true;
    }
    else if(this.state == this.states.ImeiInfo) {
      this.state = this.states.ConfirmNumber;
      this.isBackVisible = true;
    }
    else if(this.state == this.states.ProvideAdditionalInfo) {
      this.state = this.states.ImeiInfo;
      this.isBackVisible = true;
    }
    else if(this.states.AdditionalDeviceSelection){
      this.state = this.states.AdditionalDeviceQuestionnaire;
      this.isBackVisible = false;
    }
  }

  gotoTop() {
    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }

  public goToDashboard(){

    let dialogData: ConfirmationDialogData;

    dialogData = {
        title: "RegisterCellularDevice_AdditionalDevice_Questionnaire_Modal_Title",
        message: "RegisterCellularDevice_AdditionalDevice_Questionnaire_Modal_Message",
        cancelButtonText: "Global_Button_SkipForNow",
        confirmButtonText: "Global_Button_ContinueTest",
        contextData: { titleLeftAligned: false }
      };

    const confirmationDialog = this.dialog.open<ConfirmationDialogComponent, ConfirmationDialogData, boolean>(ConfirmationDialogComponent, {
      data: dialogData
    });
    this.subscriptions.push(confirmationDialog.afterClosed().subscribe({
      next: (result) => {
        if (result.toString() === 'false') {
          this.router.navigate([dashboardRouteWithSlash]);
        }
      }
    }));
  }

  closeSideNav() {
    this.sidenav.close();
  }

  public handleImeiLookupCompleted(result: ImeiResult) {
    this.imeiStepResult = result;
    this.changeState(this.states.ProvideAdditionalInfo);
  }

  public handleDeviceInfoFormSubmitted(deviceFormResult: DeviceInfoFormResult) {
    const isEprism = this.storageService.getLocal<string>(`IsEprismOnly`);
    if (this.registerDeviceInProgress || isEprism == 'true') {
      return;
    }
    this.registerDeviceInProgress = true;

    const updateDeviceDto: UpdateDeviceBodyDto = {
      id: this.device.deviceId,
      catalogSource: 'ESS',
      inventoryId: this.device.inventoryId,
      deviceTypeCode: 'SMPH',
      lineNumber: this.utilityService.getPhoneNumberInE164(this.phoneNumber),
      diagnosticStatus: "PENDING",
      modelIdentifier: deviceFormResult.equipmentInfo?.ModelIdentifier,
      make: deviceFormResult.equipmentInfo?.Manufacturer,
      model: deviceFormResult.equipmentInfo?.MarketingName,
      description: deviceFormResult.equipmentInfo?.Description,
      imei: this.imeiStepResult.imei,
      carrier: deviceFormResult.equipmentInfo?.Attributes?.Carrier,
      color: deviceFormResult.equipmentInfo?.Attributes?.Color ?? deviceFormResult.equipmentInfo?.Attributes?.Material,
      storageSize: deviceFormResult.equipmentInfo?.Attributes?.StorageSize,
      nickName: deviceFormResult.deviceNickname,
    };
    // TODO: make a buildCreateDeviceBodyDtoFromSerialSearchEquipmentInfo(....) method and add it to the DeviceInventoryService similar to the buildCreateDeviceBodyDtoFromPghUserDeviceBase(...) method

    this.deviceInventoryService.updateDevice(updateDeviceDto).pipe(
      tap(device => this.selectedDeviceService.updateAllDevices(this.deviceInventoryService.buildPghUserDeviceBaseFromDeviceDto(device))),
      tap(() => this.deviceInventoryService.clearCache()),
    ).subscribe({
      next: (res) => {
        sessionStorage.setItem('Disable_Device_Cache', 'true');
        this.registerDeviceInProgress = false;
        this.changeState(this.states.TextMessage);
      },
      error: (error) => {
        this.registerDeviceInProgress = false;
        throw error;
      },
    });
  }

}
